/**
 * Cart Manager System
 *
 * Sistema orientado a objetos para gerenciar operações de carrinho no Shopify.
 * Segue princípios SOLID para garantir código manutenível, testável e escalável.
 *
 * Componentes:
 * - CartAPI: Comunicação com Shopify Cart API
 * - CartUI: Gerenciamento de feedback visual
 * - CartNotifier: Sistema de eventos customizados
 * - CartManager: Orquestrador principal (Facade Pattern)
 *
 * @version 1.0.0
 * @author Wicomm
 */

/**
 * CartAPI - Single Responsibility: Comunicação com Shopify Cart API
 *
 * Responsável apenas por fazer requisições HTTP para a Cart API do Shopify.
 * Não conhece UI nem notificações.
 */
class CartAPI {
  constructor(config = {}) {
    this.baseUrl = config.baseUrl || '';
    this.timeout = config.timeout || 5000;
  }

  /**
   * Adiciona item ao carrinho
   * @param {string|number} variantId - ID da variante do produto
   * @param {number} quantity - Quantidade a adicionar
   * @param {object} properties - Propriedades customizadas (opcional)
   * @returns {Promise<object>} Dados do item adicionado
   */
  async add(variantId, quantity = 1, properties = {}) {
    try {
      const response = await this._fetchWithTimeout(`${this.baseUrl}/cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: properties
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Erro ao adicionar ao carrinho');
      }

      return await response.json();
    } catch (error) {
      console.error('[CartAPI] Add error:', error);
      throw error;
    }
  }

  /**
   * Remove item do carrinho
   * @param {string} lineItemKey - Chave do item no carrinho
   * @returns {Promise<object>} Estado atualizado do carrinho
   */
  async remove(lineItemKey) {
    return this.update(lineItemKey, 0);
  }

  /**
   * Atualiza quantidade de um item
   * @param {string} lineItemKey - Chave do item no carrinho
   * @param {number} quantity - Nova quantidade
   * @returns {Promise<object>} Estado atualizado do carrinho
   */
  async update(lineItemKey, quantity, properties = null) {
    try {
      const payload = {
        id: lineItemKey,
        quantity: quantity
      };

      if (properties !== null) {
        payload.properties = properties;
      }

      const response = await this._fetchWithTimeout(`${this.baseUrl}/cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errorMessage = 'Erro ao atualizar carrinho';

        try {
          const error = await response.json();
          errorMessage = error.description || error.message || errorMessage;
        } catch (_) {
          // noop: fallback para a mensagem padrão
        }

        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('[CartAPI] Update error:', error);
      throw error;
    }
  }

  /**
   * Obtém estado atual do carrinho
   * @returns {Promise<object>} Estado completo do carrinho
   */
  async get() {
    try {
      const response = await this._fetchWithTimeout(`${this.baseUrl}/cart.js`);

      if (!response.ok) {
        throw new Error('Erro ao buscar carrinho');
      }

      return await response.json();
    } catch (error) {
      console.error('[CartAPI] Get error:', error);
      throw error;
    }
  }

  /**
   * Limpa o carrinho completamente
   * @returns {Promise<object>} Carrinho vazio
   */
  async clear() {
    try {
      const response = await this._fetchWithTimeout(`${this.baseUrl}/cart/clear.js`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error('Erro ao limpar carrinho');
      }

      return await response.json();
    } catch (error) {
      console.error('[CartAPI] Clear error:', error);
      throw error;
    }
  }

  /**
   * Fetch com timeout
   * @private
   */
  _fetchWithTimeout(url, options = {}) {
    return Promise.race([
      fetch(url, options),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), this.timeout)
      )
    ]);
  }
}

/**
 * CartUI - Single Responsibility: Gerenciamento de feedback visual
 *
 * Responsável apenas por manipular estados visuais de botões e elementos UI.
 * Não conhece API nem lógica de negócio.
 */
class CartUI {
  constructor(config = {}) {
    this.successDuration = config.successDuration || 2000;
    this.errorDuration = config.errorDuration || 2000;
    this.icons = {
      success: config.successIcon || this._getSuccessIcon(),
      error: config.errorIcon || this._getErrorIcon(),
      loading: config.loadingIcon || this._getLoadingIcon()
    };
  }

  /**
   * Mostra estado de loading no botão
   * @param {HTMLElement} button - Botão a ser atualizado
   */
  showLoading(button) {
    if (!button) return;

    button.disabled = true;
    button.dataset.originalHtml = button.innerHTML;
    button.innerHTML = this.icons.loading;
    button.classList.add('loading');
  }

  /**
   * Mostra estado de sucesso no botão
   * @param {HTMLElement} button - Botão a ser atualizado
   * @param {number} duration - Duração em ms (opcional)
   */
  showSuccess(button, duration = this.successDuration) {
    if (!button) return;

    button.innerHTML = this.icons.success;
    button.classList.remove('loading');
    button.classList.add('success');

    setTimeout(() => this.reset(button), duration);
  }

  /**
   * Mostra estado de erro no botão
   * @param {HTMLElement} button - Botão a ser atualizado
   * @param {string} message - Mensagem de erro (opcional)
   * @param {number} duration - Duração em ms (opcional)
   */
  showError(button, message = '', duration = this.errorDuration) {
    if (!button) return;

    button.innerHTML = this.icons.error;
    button.classList.remove('loading');
    button.classList.add('error');

    console.error('[CartUI] Showing error state:', message);

    setTimeout(() => this.reset(button), duration);
  }

  /**
   * Reseta botão ao estado original
   * @param {HTMLElement} button - Botão a ser resetado
   */
  reset(button) {
    if (!button) return;

    const originalHtml = button.dataset.originalHtml;
    if (originalHtml) {
      button.innerHTML = originalHtml;
      delete button.dataset.originalHtml;
    }

    button.disabled = false;
    button.classList.remove('loading', 'success', 'error');
  }

  /**
   * Ícones padrão (podem ser sobrescritos via config)
   * @private
   */
  _getSuccessIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
  }

  _getErrorIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f44336" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
  }

  _getLoadingIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>';
  }
}

/**
 * CartNotifier - Single Responsibility: Sistema de eventos
 *
 * Implementa Observer Pattern para comunicação desacoplada.
 * Permite que componentes reajam a mudanças no carrinho sem dependências diretas.
 */
class CartNotifier {
  constructor() {
    this.listeners = new Map();
  }

  /**
   * Registra um listener para um evento
   * @param {string} eventName - Nome do evento
   * @param {function} callback - Função a ser chamada
   */
  on(eventName, callback) {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, []);
    }
    this.listeners.get(eventName).push(callback);
  }

  /**
   * Remove um listener
   * @param {string} eventName - Nome do evento
   * @param {function} callback - Função a ser removida
   */
  off(eventName, callback) {
    if (!this.listeners.has(eventName)) return;

    const callbacks = this.listeners.get(eventName);
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  /**
   * Emite um evento para todos os listeners
   * @param {string} eventName - Nome do evento
   * @param {object} data - Dados do evento
   */
  emit(eventName, data = {}) {
    // Listeners internos
    if (this.listeners.has(eventName)) {
      this.listeners.get(eventName).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[CartNotifier] Error in listener for "${eventName}":`, error);
        }
      });
    }

    // Evento DOM nativo para compatibilidade
    document.dispatchEvent(new CustomEvent(`cart:${eventName}`, {
      detail: data,
      bubbles: true
    }));
  }
}

/**
 * CartManager - Facade Pattern: Orquestrador principal
 *
 * Coordena CartAPI, CartUI e CartNotifier.
 * Fornece API simples e unificada para operações de carrinho.
 *
 * Dependency Inversion: Depende de abstrações (interfaces), não implementações concretas.
 */
class CartManager {
  constructor(config = {}) {
    // Dependency Injection - permite substituir implementações
    this.api = config.api || new CartAPI(config.apiConfig);
    this.ui = config.ui || new CartUI(config.uiConfig);
    this.notifier = config.notifier || new CartNotifier();

    this._initGlobalListener();
  }

  /**
   * Inicializa listener global para capturar adições de outros locais (ex: collections)
   * Intercepta fetch requests para endpoints do carrinho
   * @private
   */
  _initGlobalListener() {
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = async function (...args) {
      const response = await originalFetch.apply(this, args);

      try {
        const url = args[0] instanceof Request ? args[0].url : args[0];

        if (typeof url === 'string' &&
          !url.includes('silent=true') &&
          (url.includes('/cart/add') ||
            url.includes('/cart/update') ||
            url.includes('/cart/change') ||
            url.includes('/cart/clear'))) {

          const clone = response.clone();
          if (clone.ok) {
            // Aguarda um pouco para garantir que o Shopify processou
            setTimeout(async () => {
              const cart = await self.api.get();
              self.notifier.emit('updated', { cart });

              // Se foi adição, abre o drawer
              if (url.includes('/cart/add')) {
                self.notifier.emit('added', { cart });
              }
            }, 500);
          }
        }
      } catch (e) {
        console.error('[CartManager] Global listener error:', e);
      }

      return response;
    };
  }

  /**
   * Adiciona produto ao carrinho com feedback completo
   * @param {string|number} variantId - ID da variante
   * @param {object} options - Opções adicionais
   * @returns {Promise<object>} Resultado da operação
   */
  async addToCart(variantId, options = {}) {
    const {
      quantity = 1,
      properties = {},
      button = null,
      onSuccess = null,
      onError = null
    } = options;

    // Mostra loading
    if (button) {
      this.ui.showLoading(button);
    }

    try {
      // Chama API
      const result = await this.api.add(variantId, quantity, properties);

      // Feedback visual de sucesso
      if (button) {
        this.ui.showSuccess(button);
      }

      // Notifica sucesso
      this.notifier.emit('added', {
        variant: result,
        quantity: quantity,
        action: 'add'
      });

      // Callback customizado
      if (onSuccess) {
        onSuccess(result);
      }

      // Atualiza contadores (compatibilidade com themes antigos)
      if (typeof window.updateCart === 'function') {
        window.updateCart();
      }

      return { success: true, data: result };

    } catch (error) {
      // Feedback visual de erro
      if (button) {
        this.ui.showError(button, error.message);
      }

      // Notifica erro
      this.notifier.emit('error', {
        action: 'add',
        error: error.message,
        variantId: variantId
      });

      // Callback customizado
      if (onError) {
        onError(error);
      }

      console.error('[CartManager] Failed to add item:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Remove item do carrinho
   * @param {string} lineItemKey - Chave do item
   * @param {object} options - Opções adicionais
   */
  async removeFromCart(lineItemKey, options = {}) {
    const { onSuccess = null, onError = null } = options;

    try {
      const result = await this.api.remove(lineItemKey);

      this.notifier.emit('removed', {
        lineItemKey: lineItemKey,
        action: 'remove',
        cart: result
      });

      if (onSuccess) {
        onSuccess(result);
      }

      return { success: true, data: result };

    } catch (error) {
      this.notifier.emit('error', {
        action: 'remove',
        error: error.message,
        lineItemKey: lineItemKey
      });

      if (onError) {
        onError(error);
      }

      console.error('[CartManager] Failed to remove item:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Atualiza quantidade de um item
   * @param {string} lineItemKey - Chave do item
   * @param {number} quantity - Nova quantidade
   * @param {object} options - Opções adicionais
   */
  async updateQuantity(lineItemKey, quantity, options = {}) {
    const { onSuccess = null, onError = null } = options;

    try {
      const result = await this.api.update(lineItemKey, quantity);

      this.notifier.emit('updated', {
        lineItemKey: lineItemKey,
        quantity: quantity,
        action: 'update',
        cart: result
      });

      if (onSuccess) {
        onSuccess(result);
      }

      return { success: true, data: result };

    } catch (error) {
      this.notifier.emit('error', {
        action: 'update',
        error: error.message,
        lineItemKey: lineItemKey
      });

      if (onError) {
        onError(error);
      }

      console.error('[CartManager] Failed to update quantity:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Atualiza as propriedades de uma linha do carrinho.
   * @param {string} lineItemKey - Chave do item
   * @param {number} quantity - Quantidade atual da linha
   * @param {object} properties - Propriedades atualizadas
   * @param {object} options - Opções adicionais
   */
  async updateLineProperties(lineItemKey, quantity, properties, options = {}) {
    const { onSuccess = null, onError = null } = options;

    try {
      const result = await this.api.update(lineItemKey, quantity, properties);

      this.notifier.emit('updated', {
        lineItemKey: lineItemKey,
        quantity: quantity,
        properties: properties,
        action: 'update-properties',
        cart: result
      });

      if (onSuccess) {
        onSuccess(result);
      }

      return { success: true, data: result };

    } catch (error) {
      this.notifier.emit('error', {
        action: 'update-properties',
        error: error.message,
        lineItemKey: lineItemKey
      });

      if (onError) {
        onError(error);
      }

      console.error('[CartManager] Failed to update line properties:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Atualiza propriedades usando o índice da linha do carrinho.
   * @param {number} lineIndex - Índice 1-based da linha no carrinho
   * @param {number} quantity - Quantidade atual da linha
   * @param {object} properties - Propriedades atualizadas
   * @param {object} options - Opções adicionais
   */
  async updateLinePropertiesByIndex(lineIndex, quantity, properties, options = {}) {
    const { onSuccess = null, onError = null } = options;

    try {
      const response = await this.api._fetchWithTimeout(`${this.api.baseUrl}/cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: lineIndex,
          quantity: quantity,
          properties: properties
        })
      });

      if (!response.ok) {
        let errorMessage = 'Erro ao atualizar carrinho';

        try {
          const error = await response.json();
          errorMessage = error.description || error.message || errorMessage;
        } catch (_) {
          // noop: fallback para a mensagem padrão
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();

      this.notifier.emit('updated', {
        lineIndex: lineIndex,
        quantity: quantity,
        properties: properties,
        action: 'update-properties',
        cart: result
      });

      if (onSuccess) {
        onSuccess(result);
      }

      return { success: true, data: result };

    } catch (error) {
      this.notifier.emit('error', {
        action: 'update-properties',
        error: error.message,
        lineIndex: lineIndex
      });

      if (onError) {
        onError(error);
      }

      console.error('[CartManager] Failed to update line properties by index:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Obtém estado atual do carrinho
   */
  async getCart() {
    try {
      const cart = await this.api.get();
      return { success: true, data: cart };
    } catch (error) {
      console.error('[CartManager] Failed to get cart:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Limpa o carrinho
   */
  async clearCart(options = {}) {
    const { onSuccess = null, onError = null } = options;

    try {
      const result = await this.api.clear();

      this.notifier.emit('cleared', {
        action: 'clear',
        cart: result
      });

      if (onSuccess) {
        onSuccess(result);
      }

      return { success: true, data: result };

    } catch (error) {
      this.notifier.emit('error', {
        action: 'clear',
        error: error.message
      });

      if (onError) {
        onError(error);
      }

      console.error('[CartManager] Failed to clear cart:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Registra listener para eventos de carrinho
   * @param {string} eventName - Nome do evento (added, removed, updated, cleared, error)
   * @param {function} callback - Função callback
   */
  on(eventName, callback) {
    this.notifier.on(eventName, callback);
  }

  /**
   * Remove listener
   */
  off(eventName, callback) {
    this.notifier.off(eventName, callback);
  }
}

/**
 * Instância global singleton do CartManager
 * Facilita uso em múltiplos componentes sem re-instanciar
 */
window.CartManager = window.CartManager || new CartManager();

// Export para uso em módulos (se necessário)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CartAPI, CartUI, CartNotifier, CartManager };
}
